import useModal from "@/hooks/useModal";
import { useState } from "react";
import { FacilityDetailsHeaderQueryResponse } from "../queries";
import Button from "@/components/Button";
import IconButton from "@/components/IconButton";
import { gql, useMutation } from "@apollo/client";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";

const ARCHIVE_FACILITY_MUTATION = gql`
  mutation ArchiveFacility($id: ID!) {
    archiveFacility(id: $id) {
      success
      message
      facility {
        id
        archivedAt
      }
    }
  }
`;

const ArchiveFacilityButton = ({ facility }: { facility: FacilityDetailsHeaderQueryResponse["facility"] }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { Modal, close } = useModal();
  const [archiveFacility, { loading }] = useMutation(ARCHIVE_FACILITY_MUTATION, {
    onCompleted: (data) => {
      if (data.archiveFacility.success) {
        showSnackbar(data.archiveFacility.message, { close: true });
      } else {
        const apiMessage = data.archiveFacility.message;
        showErrorSnackbar("Facility not archived", {
          subtitle: apiMessage || "Please try again later."
        });
      }
      close();
    },
    onError: () => {
      showErrorSnackbar("Facility not archived", { subtitle: "Please try again later." });
    }
  });

  const canArchive = useHasCapabilityBool({ domain: DOMAIN.FACILITIES, permission: PERMISSION.manageFacilities });
  if (!canArchive) return null;

  if (facility.archivedAt !== null) return null;

  return (
    <>
      {isDialogOpen && (
        <Modal
          headline={"Archive Facility"}
          action={
            <Button
              variant="filled"
              theme="error"
              icon="archive"
              disabled={loading}
              onClick={() => archiveFacility({ variables: { id: facility.id } })}
            >
              Archive
            </Button>
          }
          onClosed={() => setIsDialogOpen(false)}
          hasFormElements={false}
        >
          <p>Are you sure you want to archive this facility?</p>
          <br />
          <p>
            Archived facilities are not longer visible to the public. However, they are still preserved in the system
            for historical record-keeping.
          </p>
        </Modal>
      )}
      <div className="hidden">
        <IconButton name="archive" onClick={() => setIsDialogOpen(true)} data-testid="archive-facility-button" />
      </div>
    </>
  );
};

export default ArchiveFacilityButton;
